import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Alert, Badge, Card, EmptyState, Spinner, cx } from '../../components/ui'
import { api } from '../../lib/api'
import { date, num, plural, relative } from '../../lib/format'
import type { Overview as OverviewData } from '../../lib/types'

export function Overview() {
  const [params] = useSearchParams()
  const welcome = params.get('welcome') === '1'

  const { data, isLoading, error } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<OverviewData>('/account/overview'),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="grid place-items-center py-24">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    )
  }

  if (error || !data) {
    return <Alert tone="danger">Не вдалося завантажити дані кабінету.</Alert>
  }

  const { quota, plan, usage, recentCalls, apiKeys } = data
  const usedPercent = quota.unlimited
    ? 0
    : Math.min(100, Math.round((quota.used / Math.max(quota.included, 1)) * 100))
  const testKey = apiKeys.find((k) => k.environment === 'test' && !k.revokedAt)

  return (
    <div className="space-y-6">
      {welcome && (
        <Alert tone="success" title="Вітаємо в QR24!">
          Ми вже створили для вас тестовий ключ{' '}
          {testKey && <code className="code">{testKey.prefix}</code>} — він проходить
          повну валідацію, але нічого не тарифікує.{' '}
          <Link to="/app/keys" className="font-semibold underline">
            Створити бойовий ключ →
          </Link>
        </Alert>
      )}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Огляд</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Період: {date(quota.periodStart)} — {date(quota.periodEnd)}
          </p>
        </div>
        <Badge tone="brand" className="text-sm">
          Тариф «{plan.name}»
        </Badge>
      </header>

      {/* ---------------------------------------------------------- stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Використано цього місяця"
          value={num(quota.used)}
          suffix={quota.unlimited ? 'без ліміту' : `з ${num(quota.included)}`}
          progress={quota.unlimited ? undefined : usedPercent}
        />
        <StatTile label="Залишок квоти" value={quota.unlimited ? '∞' : num(quota.remaining)} />
        <StatTile
          label="Кредити"
          value={num(quota.credits)}
          suffix={plural(quota.credits, 'кредит', 'кредити', 'кредитів')}
        />
        <StatTile
          label="Активних ключів"
          value={String(apiKeys.filter((k) => !k.revokedAt).length)}
          suffix={`ліміт ${plan.maxApiKeys}`}
        />
      </div>

      {usedPercent >= 80 && !quota.unlimited && (
        <Alert tone="warning" title={`Використано ${usedPercent}% місячної квоти`}>
          Щоб API не почав повертати <code className="code">402 quota_exceeded</code>,
          підвищте тариф або поповніть кредити.{' '}
          <Link to="/app/billing" className="font-semibold underline">
            Перейти до оплати →
          </Link>
        </Alert>
      )}

      {/* ---------------------------------------------------------- chart */}
      <Card
        title="Запити за 30 днів"
        description={`Усього ${num(usage.totals.units)} згенерованих QR, ${num(usage.totals.errors)} помилок`}
      >
        {usage.series.length === 0 ? (
          <EmptyState
            title="Ще немає викликів API"
            description="Щойно ви зробите перший запит, тут зʼявиться графік."
            action={
              <Link to="/docs" className="btn-secondary mt-2">
                Як зробити перший запит
              </Link>
            }
          />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usage.series} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="units" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" vertical={false} />
                <XAxis
                  dataKey="day"
                  tickFormatter={(value: string) => value.slice(5)}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid rgb(203 213 225)',
                    fontSize: 13,
                  }}
                  labelFormatter={(value: string) => date(value)}
                  formatter={(value: number) => [num(value), 'QR-кодів']}
                />
                <Area
                  type="monotone"
                  dataKey="units"
                  stroke="#0d9488"
                  strokeWidth={2}
                  fill="url(#units)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* ----------------------------------------------------- recent log */}
      <Card
        title="Останні виклики"
        description="Живий хвіст логу — зручно під час налагодження інтеграції"
        actions={
          <Link to="/app/usage" className="btn-ghost text-sm">
            Весь журнал →
          </Link>
        }
        padded={false}
      >
        {recentCalls.length === 0 ? (
          <EmptyState title="Журнал порожній" description="Тут зʼявляться ваші запити до API." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Час</th>
                  <th className="px-5 py-2.5 font-medium">Ендпоінт</th>
                  <th className="px-5 py-2.5 font-medium">Статус</th>
                  <th className="px-5 py-2.5 font-medium">Ключ</th>
                  <th className="px-5 py-2.5 text-right font-medium">Затримка</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {recentCalls.map((call, i) => (
                  <tr key={`${call.requestId}-${i}`}>
                    <td className="whitespace-nowrap px-5 py-2.5 text-slate-500 dark:text-slate-400">
                      {relative(call.at)}
                    </td>
                    <td className="px-5 py-2.5 font-mono text-xs">
                      {call.endpoint}
                      {call.quantity > 1 && (
                        <span className="ml-1.5 text-slate-400">×{call.quantity}</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <Badge tone={call.status < 400 ? 'success' : call.status < 500 ? 'warning' : 'danger'}>
                        {call.status}
                      </Badge>
                      {call.errorCode && (
                        <span className="ml-2 font-mono text-xs text-slate-400">
                          {call.errorCode}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                      {call.key?.name ?? '—'}
                      {!call.billable && (
                        <Badge className="ml-2" tone="neutral">
                          test
                        </Badge>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-slate-500">
                      {call.latencyMs != null ? `${call.latencyMs} мс` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function StatTile({
  label,
  value,
  suffix,
  progress,
}: {
  label: string
  value: string
  suffix?: string
  progress?: number
}) {
  return (
    <div className="card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold tabular-nums">{value}</p>
      {suffix && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{suffix}</p>}
      {progress !== undefined && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={cx(
              'h-full rounded-full transition-all',
              progress >= 90 ? 'bg-rose-500' : progress >= 70 ? 'bg-amber-500' : 'bg-brand-500',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  )
}
